import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ApiAuthService } from './api-auth.service';
import { ActiveRoleUser, UserRole } from '../user-role.types';
import { PermissionCode, RoleProfile } from '../permission.types';
import { Observable, catchError, shareReplay, tap, throwError } from 'rxjs';

export interface ApiMeDto {
  userId: number | string;
  displayName: string;
  role: UserRole;
  permissions: string[];
  agentId?: string;
  portfolioIds?: string[];
  creditorIds?: string[];
}

@Injectable({ providedIn: 'root' })
export class ApiSessionService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(ApiAuthService);
  private readonly profile = signal<ApiMeDto | null>(null);
  private profileRequest$: Observable<ApiMeDto> | null = null;
  readonly me: Signal<ApiMeDto | null> = this.profile.asReadonly();
  readonly user: Signal<ActiveRoleUser> = computed(() => {
    const me = this.profile();
    return { name: me?.displayName ?? '', avatarInitials: initials(me?.displayName), userId: me ? String(me.userId) : undefined, agentId: me?.agentId, portfolioIds: me?.portfolioIds, creditorIds: me?.creditorIds };
  });

  constructor() { if (this.auth.accessToken()) this.ensureProfileLoaded().subscribe({ error: () => undefined }); }

  loadMe() { return this.http.get<ApiMeDto>(`${environment.apiBaseUrl}/api/v1/me`); }
  refreshMe() { return this.loadMe().pipe(tap(me => this.profile.set(me))); }
  ensureProfileLoaded(): Observable<ApiMeDto> {
    if (this.profile()) return new Observable(subscriber => { subscriber.next(this.profile()!); subscriber.complete(); });
    if (!this.profileRequest$) {
      this.profileRequest$ = this.loadMe().pipe(
        tap(me => this.profile.set(me)),
        catchError(error => { this.clearProfile(); return throwError(() => error); }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.profileRequest$;
  }
  clearProfile(): void { this.profile.set(null); this.profileRequest$ = null; }
  setMe(me: ApiMeDto): void { this.profile.set(me); }
  role(): UserRole { return this.profile()?.role ?? 'AGENT'; }
  permissions(): readonly PermissionCode[] { return (this.profile()?.permissions ?? []) as PermissionCode[]; }
  roleProfile(): RoleProfile { return ({ AGENT: 'agent', SUPERVISOR: 'superviseur', MANAGER: 'manager', ADMIN: 'administrateur' } as const)[this.role()]; }
}

function initials(name?: string): string {
  return (name ?? 'UA').split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'UA';
}
